// Isometric 3D view — Three.js scene built from the calibrated plan:
// plan image as floor texture, traced walls extruded, and real AI-generated
// 3D models of the Makasa products (TRELLIS GLB) with procedural fallback.
import React, { Suspense, useEffect, useMemo, useRef } from "react";
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import { OrbitControls, Grid, useGLTF } from "@react-three/drei";
import * as THREE from "three";
import { WALL_DEFAULTS } from "./data";

/* ── Real product 3D model (GLB from the product photo) ─────────────── */
class ModelBoundary extends React.Component {
  constructor(p) { super(p); this.state = { err: false }; }
  static getDerivedStateFromError() { return { err: true }; }
  render() { return this.state.err ? this.props.fallback : this.props.children; }
}

function ProductModel({ url, w, d, h }) {
  const gltf = useGLTF(url);
  const obj = useMemo(() => {
    const o = gltf.scene.clone(true);
    o.traverse((c) => { if (c.isMesh) { c.castShadow = true; c.receiveShadow = true; } });
    return o;
  }, [gltf]);
  const { sf, pos } = useMemo(() => {
    const box = new THREE.Box3().setFromObject(obj);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    // uniform scale: fit the model inside the true metric footprint + height
    const sf = Math.min(w / (size.x || 1), d / (size.z || 1), h / (size.y || 1));
    return { sf, pos: [-center.x * sf, -box.min.y * sf, -center.z * sf] };
  }, [obj, w, d, h]);
  return <primitive object={obj} scale={[sf, sf, sf]} position={pos} />;
}

/* ── Procedural fallback furniture (shown while GLB loads / if missing) ── */
function ProceduralBody({ it }) {
  const { width_m: w, depth_m: d, height_m: h, color, category, round } = it;
  const c = color || "#999";
  const mat = { color: c, roughness: 0.8 };
  if (category === "Decor" && h < 0.1)
    return <mesh position={[0, 0.011, 0]} receiveShadow>
      {round ? <cylinderGeometry args={[w / 2, w / 2, 0.02, 32]} /> : <boxGeometry args={[w, 0.02, d]} />}
      <meshStandardMaterial {...mat} /></mesh>;
  if (category === "Lighting")
    return <>
      <mesh position={[0, h / 2, 0]} castShadow><cylinderGeometry args={[0.03, 0.05, h, 12]} /><meshStandardMaterial {...mat} /></mesh>
      <mesh position={[0, h - 0.12, 0]} castShadow><coneGeometry args={[0.18, 0.24, 16, 1, true]} /><meshStandardMaterial color="#F5EFE3" side={THREE.DoubleSide} /></mesh>
    </>;
  if (round)
    return <>
      <mesh position={[0, h - 0.05, 0]} castShadow><cylinderGeometry args={[w / 2, w / 2, 0.1, 32]} /><meshStandardMaterial {...mat} /></mesh>
      <mesh position={[0, (h - 0.1) / 2, 0]} castShadow><cylinderGeometry args={[0.06, 0.08, h - 0.1, 12]} /><meshStandardMaterial color="#6B5644" roughness={0.8} /></mesh>
    </>;
  if (category === "Seating")
    return <>
      <mesh position={[0, 0.28, 0]} castShadow><boxGeometry args={[w, 0.42, d]} /><meshStandardMaterial {...mat} /></mesh>
      <mesh position={[0, h - 0.28, -d / 2 + 0.12]} castShadow><boxGeometry args={[w, h - 0.3, 0.24]} /><meshStandardMaterial {...mat} /></mesh>
    </>;
  if (category === "Bedding")
    return <>
      <mesh position={[0, h / 2 - 0.1, 0]} castShadow><boxGeometry args={[w, h - 0.2, d]} /><meshStandardMaterial {...mat} /></mesh>
      <mesh position={[0, h - 0.08, -d / 2 + 0.35]} castShadow><boxGeometry args={[w * 0.8, 0.16, 0.55]} /><meshStandardMaterial color="#FFFFFF" roughness={0.9} /></mesh>
    </>;
  if (category === "Dining" && w < 0.6)
    return <>
      <mesh position={[0, h * 0.55, 0]} castShadow><boxGeometry args={[w, h * 0.55, d]} /><meshStandardMaterial {...mat} /></mesh>
      <mesh position={[0, h - 0.2, -d / 2 + 0.06]} castShadow><boxGeometry args={[w, h * 0.45, 0.1]} /><meshStandardMaterial {...mat} /></mesh>
    </>;
  return <mesh position={[0, h / 2, 0]} castShadow><boxGeometry args={[w, h, d]} /><meshStandardMaterial {...mat} /></mesh>;
}

function FurnitureMesh({ it }) {
  const rotY = -((it.rot || 0) * Math.PI) / 180;
  if (!it.model)
    return <group position={[it.x_m, 0, it.y_m]} rotation={[0, rotY, 0]}><ProceduralBody it={it} /></group>;
  return (
    <group position={[it.x_m, 0, it.y_m]} rotation={[0, rotY, 0]}>
      <ModelBoundary fallback={<ProceduralBody it={it} />}>
        <Suspense fallback={<ProceduralBody it={it} />}>
          <ProductModel url={it.model} w={it.width_m} d={it.depth_m} h={it.height_m} />
        </Suspense>
      </ModelBoundary>
    </group>
  );
}

/* ── First-person walk: click to capture the mouse (pointer lock, desktop),
 * drag to look (touch), ZQSD/WASD/arrows or the on-screen joystick to move,
 * furniture + wall collision, eye height 1.6 m, clamped inside plan bounds. */
function WalkControls({ W, H, moveRef, items, walls }) {
  const { camera, gl } = useThree();
  const keys = useRef({});
  const look = useRef({ yaw: -Math.PI / 4, pitch: -0.06 });
  const colliders = useMemo(() => {
    const circles = (items || [])
      .filter((it) => !(it.category === "Decor" && it.height_m < 0.1)) // rugs don't block
      .map((it) => ({
        x: it.x_m - W / 2, z: it.y_m - H / 2,
        r: Math.min(Math.max(it.width_m, it.depth_m) / 2, 0.35) + 0.18,
      }));
    const segs = [];
    (walls || []).forEach((w) => {
      const p = w.points_m;
      for (let i = 0; i + 3 < p.length; i += 2) {
        const ax = p[i] - W / 2, az = p[i + 1] - H / 2, bx = p[i + 2] - W / 2, bz = p[i + 3] - H / 2;
        const doors = (w.openings || []).filter((o) => o.seg === i / 2 && o.type === "door");
        if (!doors.length) { segs.push([ax, az, bx, bz]); continue; }
        // doors are walkable: split the collision segment around each doorway
        const len = Math.hypot(bx - ax, bz - az) || 1;
        const ux = (bx - ax) / len, uz = (bz - az) / len;
        let cur = 0;
        doors.sort((x, y) => x.off_m - y.off_m).forEach((o) => {
          const s = Math.max(0, o.off_m - o.width_m / 2), e = Math.min(len, o.off_m + o.width_m / 2);
          if (s > cur) segs.push([ax + ux * cur, az + uz * cur, ax + ux * s, az + uz * s]);
          cur = e;
        });
        if (cur < len) segs.push([ax + ux * cur, az + uz * cur, bx, bz]);
      }
    });
    return { circles, segs };
  }, [items, walls, W, H]);

  useEffect(() => {
    camera.rotation.order = "YXZ";
    camera.position.set(W * 0.28, 1.6, H * 0.28); // start off-center, not inside the furniture cluster
    look.current = { yaw: Math.atan2(W * 0.28, H * 0.28), pitch: -0.06 }; // face the room center
    const kd = (e) => { keys.current[e.code] = true; };
    const ku = (e) => { keys.current[e.code] = false; };
    let dragging = false, lx = 0, ly = 0;
    const el = gl.domElement;
    const pd = (e) => {
      if (e.pointerType === "mouse") { el.requestPointerLock?.(); return; }
      dragging = true; lx = e.clientX; ly = e.clientY;
    };
    const pu = () => { dragging = false; };
    const pm = (e) => {
      if (document.pointerLockElement === el) {
        look.current.yaw -= e.movementX * 0.0023;
        look.current.pitch = Math.max(-1.15, Math.min(1.15, look.current.pitch - e.movementY * 0.0023));
        return;
      }
      if (!dragging) return;
      look.current.yaw -= (e.clientX - lx) * 0.004;
      look.current.pitch = Math.max(-1.15, Math.min(1.15, look.current.pitch - (e.clientY - ly) * 0.004));
      lx = e.clientX; ly = e.clientY;
    };
    window.addEventListener("keydown", kd);
    window.addEventListener("keyup", ku);
    el.addEventListener("pointerdown", pd);
    window.addEventListener("pointerup", pu);
    window.addEventListener("pointermove", pm);
    return () => {
      window.removeEventListener("keydown", kd);
      window.removeEventListener("keyup", ku);
      el.removeEventListener("pointerdown", pd);
      window.removeEventListener("pointerup", pu);
      window.removeEventListener("pointermove", pm);
      if (document.pointerLockElement === el) document.exitPointerLock?.();
    };
  }, [camera, gl]);

  useFrame((_, dt) => {
    const k = keys.current;
    const sp = 2.4 * Math.min(dt, 0.05);
    const yaw = look.current.yaw;
    const fwd = [-Math.sin(yaw), -Math.cos(yaw)];
    const right = [Math.cos(yaw), -Math.sin(yaw)];
    let mx = 0, mz = 0;
    if (k["KeyW"] || k["KeyZ"] || k["ArrowUp"]) { mx += fwd[0]; mz += fwd[1]; }
    if (k["KeyS"] || k["ArrowDown"]) { mx -= fwd[0]; mz -= fwd[1]; }
    if (k["KeyA"] || k["KeyQ"] || k["ArrowLeft"]) { mx -= right[0]; mz -= right[1]; }
    if (k["KeyD"] || k["ArrowRight"]) { mx += right[0]; mz += right[1]; }
    const j = moveRef?.current;
    if (j && (j.x || j.z)) { mx += fwd[0] * j.z + right[0] * j.x; mz += fwd[1] * j.z + right[1] * j.x; }
    if (!mx && !mz) { camera.rotation.set(look.current.pitch, look.current.yaw, 0); return; }

    const { circles, segs } = colliders;
    const free = (x, z) => {
      for (const c of circles) if (Math.hypot(x - c.x, z - c.z) < c.r) return false;
      for (const [ax, az, bx, bz] of segs) {
        const dx = bx - ax, dz = bz - az;
        const len2 = dx * dx + dz * dz || 1;
        const t = Math.max(0, Math.min(1, ((x - ax) * dx + (z - az) * dz) / len2));
        if (Math.hypot(x - (ax + t * dx), z - (az + t * dz)) < 0.22) return false;
      }
      return true;
    };
    // axis-separated slide so you glide along furniture/walls instead of sticking
    const px = camera.position.x, pz = camera.position.z;
    const nx = px + mx * sp, nz = pz + mz * sp;
    let fx = px, fz = pz;
    if (free(nx, nz)) { fx = nx; fz = nz; }
    else if (free(nx, pz)) { fx = nx; }
    else if (free(px, nz)) { fz = nz; }
    camera.position.x = Math.max(-W / 2 + 0.25, Math.min(W / 2 - 0.25, fx));
    camera.position.z = Math.max(-H / 2 + 0.25, Math.min(H / 2 - 0.25, fz));
    camera.position.y = 1.6;
    camera.rotation.set(look.current.pitch, look.current.yaw, 0);
  });
  return null;
}

const DOOR_H = 2.04;      // standard French door height
const WIN_SILL = 0.9, WIN_TOP = 2.0; // standard window sill / top

function WallSegment({ a, b, h, t, openings }) {
  const boxes = useMemo(() => {
    const dx = b[0] - a[0], dz = b[1] - a[1];
    const len = Math.hypot(dx, dz) || 1;
    const ux = dx / len, uz = dz / len;
    const rotY = -Math.atan2(dz, dx);
    const ops = (openings || []).slice().sort((x, y) => x.off_m - y.off_m);
    const pieces = [];
    let cur = 0;
    ops.forEach((o) => {
      const s = Math.max(0, o.off_m - o.width_m / 2), e = Math.min(len, o.off_m + o.width_m / 2);
      if (s > cur) pieces.push({ from: cur, to: s, y0: 0, y1: h });
      if (o.type === "door") {
        pieces.push({ from: s, to: e, y0: DOOR_H, y1: h });            // lintel above the door
      } else {
        pieces.push({ from: s, to: e, y0: 0, y1: WIN_SILL });           // sill below the window
        pieces.push({ from: s, to: e, y0: WIN_TOP, y1: h });            // lintel above
      }
      cur = e;
    });
    if (cur < len) pieces.push({ from: cur, to: len, y0: 0, y1: h });
    return pieces.map((p) => ({
      pos: [a[0] + ux * (p.from + p.to) / 2, (p.y0 + p.y1) / 2, a[1] + uz * (p.from + p.to) / 2],
      len: p.to - p.from, hh: p.y1 - p.y0, rotY,
    })).filter((p) => p.len > 0.01 && p.hh > 0.01);
  }, [a, b, h, openings]);
  return (
    <>
      {boxes.map((bx, i) => (
        <mesh key={i} position={bx.pos} rotation={[0, bx.rotY, 0]} castShadow receiveShadow>
          <boxGeometry args={[bx.len, bx.hh, t]} />
          <meshStandardMaterial color="#E9E2D8" roughness={0.95} />
        </mesh>
      ))}
    </>
  );
}

function SceneContent({ plan, items, walls, floorTexture, walk, moveRef }) {
  const W = plan.w_m, H = plan.h_m;
  const shadowExtent = Math.max(W, H) + 2;
  return (
    <>
      <ambientLight intensity={0.45} />
      <hemisphereLight args={["#FDF6EC", "#8A7F70", 0.55]} />
      <directionalLight position={[W * 0.7, 12, H * 0.4]} intensity={1.5} castShadow
                        shadow-mapSize={[2048, 2048]}
                        shadow-camera-left={-shadowExtent} shadow-camera-right={shadowExtent}
                        shadow-camera-top={shadowExtent} shadow-camera-bottom={-shadowExtent} />
      <group position={[-W / 2, 0, -H / 2]}>
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[W / 2, -0.01, H / 2]} receiveShadow>
          <planeGeometry args={[W, H]} />
          {floorTexture
            ? <meshStandardMaterial map={floorTexture} roughness={0.95} />
            : <meshStandardMaterial color="#F1ECE4" roughness={0.95} />}
        </mesh>
        {walls.map((wall) => {
          const segs = [];
          for (let i = 0; i + 3 < wall.points_m.length; i += 2) {
            segs.push([wall.points_m.slice(i, i + 2), wall.points_m.slice(i + 2, i + 4)]);
          }
          return segs.map(([a, b], i) => (
            <WallSegment key={`${wall.id}-${i}`} a={a} b={b} h={WALL_DEFAULTS.height_m} t={WALL_DEFAULTS.thickness_m}
                         openings={(wall.openings || []).filter((o) => o.seg === i)} />
          ));
        })}
        {items.map((it) => <FurnitureMesh key={it.uid} it={it} />)}
      </group>
      {!walk && (
        <Grid args={[Math.max(W, H) * 2, Math.max(W, H) * 2]} cellSize={0.5} sectionSize={1}
              cellColor="#D8D0C4" sectionColor="#B8AE9E" position={[0, -0.02, 0]} fadeDistance={60} infiniteGrid />
      )}
      {walk
        ? <WalkControls W={W} H={H} moveRef={moveRef} items={items} walls={walls} />
        : <OrbitControls makeDefault target={[0, 0, 0]} maxPolarAngle={Math.PI / 2.15} minDistance={2} maxDistance={40} />}
    </>
  );
}

const ThreeView = React.forwardRef(function ThreeView({ plan, items, walls, walk, moveRef }, ref) {
  const glRef = useRef(null);
  const floorTexture = useMemo(() => {
    if (!plan?.dataUrl) return null;
    const tex = new THREE.TextureLoader().load(plan.dataUrl);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
  }, [plan?.dataUrl]);

  // warm the GLB cache so the 3D view (and its PDF snapshot) shows real models fast
  useEffect(() => {
    items.forEach((it) => { if (it.model) useGLTF.preload(it.model); });
  }, [items]);

  useEffect(() => {
    if (ref) ref.current = { capture: () => glRef.current?.domElement?.toDataURL("image/png") };
  }, [ref]);

  const W = plan.w_m, H = plan.h_m;
  const dist = Math.max(W, H) * 1.15 + 2;
  return (
    <Canvas
      shadows="soft"
      dpr={[1, 2]}
      data-testid="floorplan-canvas-3d"
      gl={{ preserveDrawingBuffer: true, antialias: true, toneMapping: THREE.ACESFilmicToneMapping }}
      camera={{ position: [dist, dist * 0.9, dist], fov: 38 }}
      onCreated={({ gl }) => { glRef.current = gl; }}
      style={{ background: "#EFEAE2", touchAction: "none" }}
    >
      <SceneContent plan={plan} items={items} walls={walls} floorTexture={floorTexture} walk={walk} moveRef={moveRef} />
    </Canvas>
  );
});

export default ThreeView;
